import { type ButtonHTMLAttributes, forwardRef } from "react";
import { cn } from "@/lib/utils";

type ButtonVariant = "primary" | "secondary" | "danger" | "ghost" | "cancel" | "auth";
type ButtonSize = "default" | "sm" | "lg" | "icon";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: "bg-accent hover:bg-accent-hover text-white rounded-md text-sm",
  secondary: "bg-transparent text-text-secondary border border-border hover:bg-gray-100 rounded-md text-sm",
  danger: "bg-danger-light text-danger border border-danger-light rounded-md text-sm",
  ghost: "bg-transparent text-text-secondary hover:bg-gray-100 rounded-md text-sm",
  cancel: "bg-transparent text-text-secondary border border-border hover:bg-gray-100 rounded-md text-sm",
  auth: "w-full bg-accent text-white rounded-md text-sm hover:bg-accent-hover",
};

const sizeClasses: Record<ButtonSize, string> = {
  default: "px-6 py-2",
  sm: "px-3 py-1.5 text-xs",
  lg: "px-8 py-3",
  icon: "p-2",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "primary", size = "default", ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center gap-2 transition-colors duration-150 outline-none font-medium disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer",
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
      {...props}
    />
  )
);
Button.displayName = "Button";
